import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { SearchBox, mergeStyleSets } from '@fluentui/react';
import Fuse from 'fuse.js';
import { boroondaraPalette } from '../../theme/boroondaraTheme';
import { SpatialLayer, GeoJSONFeatureCollection, GeoJSONFeature } from './types';

const p = boroondaraPalette;

interface SearchBarProps {
  layers: SpatialLayer[];
  featureData: Record<string, GeoJSONFeatureCollection>;
  activeLayers: Set<string>;
  onFeatureSelect: (feature: GeoJSONFeature, layerColor: string) => void;
}

interface SearchResult {
  feature: GeoJSONFeature;
  layerName: string;
  layerColor: string;
}

const styles = mergeStyleSets({
  wrap: {
    position: 'relative' as any,
    padding: '10px 16px 6px',
  },
  dropdown: {
    position: 'absolute' as any,
    top: '100%',
    left: 16,
    right: 16,
    background: '#fff',
    border: `1px solid ${p.border}`,
    borderRadius: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    maxHeight: 240,
    overflowY: 'auto' as any,
    zIndex: 1100,
  } as any,
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: 13,
    selectors: { ':hover': { background: p.bg2 } },
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  resultName: { fontWeight: 500, color: p.text },
  resultLayer: { fontSize: 11, color: p.text3, marginLeft: 'auto' },
  noResults: {
    padding: '12px',
    fontSize: 12,
    color: p.text3,
    textAlign: 'center',
  } as any,
});

export default function SearchBar({ layers, featureData, activeLayers, onFeatureSelect }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Build searchable items from active layers
  const items = useMemo<SearchResult[]>(() => {
    const results: SearchResult[] = [];
    layers.forEach((layer) => {
      if (!activeLayers.has(layer.layer_id)) return;
      const fc = featureData[layer.layer_id];
      if (!fc?.features) return;
      fc.features.forEach((f) => {
        results.push({
          feature: f,
          layerName: layer.name,
          layerColor: layer.color,
        });
      });
    });
    return results;
  }, [layers, featureData, activeLayers]);

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: ['feature.properties.name', 'feature.properties.description'],
        threshold: 0.35,
        minMatchCharLength: 2,
      }),
    [items]
  );

  const results = useMemo(() => {
    if (!query || query.length < 2) return [];
    return fuse.search(query, { limit: 12 }).map((r) => r.item);
  }, [fuse, query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      onFeatureSelect(result.feature, result.layerColor);
      setQuery('');
      setOpen(false);
    },
    [onFeatureSelect]
  );

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <SearchBox
        placeholder="Search features..."
        value={query}
        onChange={(_, val) => {
          setQuery(val || '');
          setOpen(true);
        }}
        onFocus={() => query.length >= 2 && setOpen(true)}
        onClear={() => {
          setQuery('');
          setOpen(false);
        }}
        styles={{ root: { borderRadius: 4 } }}
      />
      {open && query.length >= 2 && (
        <div className={styles.dropdown}>
          {results.length > 0 ? (
            results.map((r, i) => (
              <div key={i} className={styles.resultItem} onClick={() => handleSelect(r)}>
                <div className={styles.dot} style={{ background: r.layerColor }} />
                <span className={styles.resultName}>{r.feature.properties?.name || 'Feature'}</span>
                <span className={styles.resultLayer}>{r.layerName}</span>
              </div>
            ))
          ) : (
            <div className={styles.noResults}>No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
